import { cva, type VariantProps } from '@veraclins-dev/cva';

import { emphasisActionVariants } from './base';
import { BUTTON_DEFAULT_CLASSES } from './shared-classes';
import { styleProps } from './styles';

// Button variants — Model A action axes (emphasis × tone).
const buttonVariants = cva({
  base: BUTTON_DEFAULT_CLASSES,
  responsive: {
    ...styleProps,
  },
  variants: {
    ...emphasisActionVariants.variants,
    // The min heights match `inputSize` (32, 40, 48 and 56px), so a button
    // beside a text field of the same size lines up with it. The padding alone
    // left each size 2px short of the field. On a touch screen the sizes below
    // 44px grow to `--touch-min` on both axes; lg and xl are already taller.
    buttonSize: {
      sm: 'min-h-8 py-1 px-2 pointer-coarse:min-h-(--touch-min) pointer-coarse:min-w-(--touch-min)',
      md: 'min-h-10 px-4 py-2 pointer-coarse:min-h-(--touch-min) pointer-coarse:min-w-(--touch-min)',
      lg: 'min-h-12 py-3 px-6',
      xl: 'min-h-14 py-4 px-8',
      icon: 'p-2 pointer-coarse:min-h-(--touch-min) pointer-coarse:min-w-(--touch-min)',
    },
    // The surface the button paints under a see-through emphasis (outline,
    // quiet, ghost). `card` gives it the card fill, so a quiet button can sit
    // on a grey page as a small card, such as an organisation switcher, the
    // way a field takes `surface="card"`. Hover stays on the card ramp rather
    // than turning grey. Filled and soft paint their own fill and ignore it.
    surface: {
      transparent: '',
      card: '',
    },
    loading: {
      true: 'cursor-not-allowed',
      false: '',
    },
    fullWidth: {
      true: 'w-full flex-1',
      false: '',
    },
    // Swaps the focus ring for a solid outline on keyboard focus only.
    noRing: {
      true: 'ring-0 focus:ring-0 focus-visible:ring-0 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
      false: '',
    },
  },
  compoundVariants: [
    ...emphasisActionVariants.compoundVariants,
    {
      emphasis: ['outline', 'quiet', 'ghost'],
      surface: 'card',
      // The label colour comes from the emphasis and tone compounds above.
      // Inside a filled Alert the label turns to the alert's own foreground,
      // so the card fill steps aside there.
      className: ['bg-card', 'hover:bg-card-hover', 'on-solid:bg-transparent'],
    },
    // Quiet already draws the hairline edge; on a card it darkens on hover,
    // as on the design's organisation chip. Outline keeps its tone edge.
    {
      emphasis: 'quiet',
      surface: 'card',
      className: 'hover:border-border-strong',
    },
  ],
  defaultVariants: {
    emphasis: 'filled',
    tone: 'neutral',
    buttonSize: 'md',
    surface: 'transparent',
    loading: false,
  },
});

type ButtonVariants = VariantProps<typeof buttonVariants>;

export { type ButtonVariants, buttonVariants };
